import { groupBy } from 'lodash'
import { sessionStorage } from 'react-storage'
import getConfig from 'next/config'

import {
  sendEvent,
  getCurrencyCode,
  regexForStripSpecialChars,
  regexForStripHTML,
  regexForStripEncodingUnicode,
  DEFAULT_CURRENCY_CODE,
  ADD_TO_CART_GA_DATA_STORAGE_KEY,
  legacySendEvent,
  priceFormatter,
} from '@utils/analytics/coreAnalytics'

import {
  EventActions,
  EventLabel,
  GA4ListInteractionEventPayload,
  GTMEcommerceItem,
} from '@utils/analytics/types'

import {
  GTMEventCategory,
  EventValues,
  GTMEventTypes,
} from '@sial/common-utils'

import {
  sendAddItemsToCartEvent,
  sendAddItemsToCartErrorEvent,
} from '@utils/tealiumAnalytics'

const {
  publicRuntimeConfig: { isTealiumEnabled },
} = getConfig()

export enum ConfiguratorProductType {
  Milliplex = 'milliplex',
  Configurator = 'Configurator',
  Sirna = 'Predesigned siRNA',
}

export type ConfiguratorEventProduct = {
  id: string
  name: string
  variant?: string
  brand: string
  quantity: number
  price: number | null
  dimension91: string
  discount?: number | null
}

const configToolStr = 'configurator tool'

const clubbingUpProductsforConfig = (
  configdata,
  products
): GTMEcommerceItem[] => {
  const itemGroups = groupBy(products || [], (item) => item?.material)
  const productsArray: GTMEcommerceItem[] = Object.values(itemGroups).map(
    (group) => {
      const item = group[0]
      const quant = group.length
      return {
        id: item?.material?.toLowerCase() || EventValues.Empty,
        name:
          configdata?.productName
            ?.toLowerCase()
            .replace(regexForStripEncodingUnicode, '')
            .replace(regexForStripHTML, '')
            .replace(regexForStripSpecialChars, '') || EventValues.Empty,
        variant: item?.material?.toLowerCase() || EventValues.Empty,
        brand: item?.brand?.toLowerCase() || EventValues.Empty,
        quantity: quant || 1,
        price: priceFormatter(item?.price),
        dimension91: 'standard',
      }
    }
  )
  return productsArray || []
}

// GAtracking for addTo cart for configurator products
export const handleGAAddToCartForConfigurator = (configProductData): void => {
  if (isTealiumEnabled) {
    // for Tealium
    let products
    let currency
    const pageName = GTMEventCategory.ConfiguratorTool?.toLowerCase()
    if (
      configProductData?.productName !== ConfiguratorProductType.Milliplex &&
      configProductData?.productName !== ConfiguratorProductType.Sirna
    ) {
      currency = getCurrencyCode(
        configProductData?.productDetails[0]?.currency || DEFAULT_CURRENCY_CODE
      )
      products = [
        {
          id: (
            configProductData?.productDetails[0]?.material || EventValues.Empty
          )?.toLowerCase(),
          name: (
            configProductData?.productName
              .replace(regexForStripEncodingUnicode, '')
              .replace(regexForStripHTML, '')
              .replace(regexForStripSpecialChars, '') || EventValues.Empty
          )?.toLowerCase(),
          materialNumber: (
            configProductData?.productDetails[0]?.material?.toLowerCase() ||
            EventValues.Empty
          )?.toLowerCase(),
          brand: (
            configProductData?.productDetails[0]?.brand || EventValues.Empty
          )?.toLowerCase(),
          quantity: configProductData?.totalQuantity || 1,
          variant:
            configProductData?.productDetails[0]?.material?.toLowerCase() ||
            EventValues.Empty,
          price: priceFormatter(configProductData?.price),
          dimension91: 'standard',
        },
      ]
    } else if (
      configProductData?.productName === ConfiguratorProductType.Sirna
    ) {
      currency = getCurrencyCode(
        configProductData?.productDetails[0]?.currency || DEFAULT_CURRENCY_CODE
      )

      const productArray = clubbingUpProductsforConfig(
        configProductData,
        configProductData.productDetails
      )
      products = productArray
    } else {
      currency = getCurrencyCode(
        configProductData.milliplexData[0]?.currency || DEFAULT_CURRENCY_CODE
      )
      products =
        configProductData.milliplexData[0]?.listMilliplexAssayModel[0]?.assays
          ?.filter((assay) =>
            configProductData.milliplexData[0]?.checkedProductIdList.includes(
              assay.panelId
            )
          )
          .map((assay) => {
            let gaPrice
            let gaFormattedPrice = '1'
            if (assay?.price) {
              const avgPrice = assay?.price
              if (avgPrice) {
                try {
                  gaPrice = avgPrice
                    .slice(0, avgPrice.length - 3)
                    .replace(/[.,]/g, '')
                  gaFormattedPrice =
                    gaPrice +
                    avgPrice.slice(avgPrice.length - 3, avgPrice.length)
                } catch {
                  gaFormattedPrice = avgPrice
                }
              } else {
                gaFormattedPrice = avgPrice
              }
            }
            return {
              id: assay?.material?.toLowerCase() || EventValues.Empty,
              name:
                assay?.panelName
                  ?.toLowerCase()
                  .replace(regexForStripEncodingUnicode, '')
                  .replace(regexForStripHTML, '')
                  .replace(regexForStripSpecialChars, '') || EventValues.Empty,
              materialNumber:
                assay?.material?.toLowerCase() || EventValues.Empty,
              brand:
                configProductData.milliplexData[0]?.brand.toLowerCase() ||
                EventValues.Empty,
              quantity: assay?.quantity || 1,
              variant: assay?.material?.toLowerCase() || EventValues.Empty,
              price: priceFormatter(gaFormattedPrice),
              dimension91: 'standard',
            }
          }) || []
    }

    sessionStorage.setItem(ADD_TO_CART_GA_DATA_STORAGE_KEY, {
      products,
      pageName,
      currency,
    })

    sendAddItemsToCartEvent({
      sessionStorageKey: ADD_TO_CART_GA_DATA_STORAGE_KEY,
    })
    sessionStorage.removeItem(ADD_TO_CART_GA_DATA_STORAGE_KEY)
  } else {
    const subTotal =
      configProductData?.productDetails.reduce(
        (acc, product) =>
          (Number(product.price) || 1) * (product.quantity ?? 1) + acc,
        0
      ) || 0
    // for GA
    if (
      configProductData?.productName !== ConfiguratorProductType.Milliplex &&
      configProductData?.productName !== ConfiguratorProductType.Sirna
    ) {
      // GA4 - Add To Cart
      legacySendEvent({
        payload: {
          event: 'add_to_cart',
          addtocart_type: 'default',
          ecommerce: {
            value: subTotal,
            currency: getCurrencyCode(
              configProductData?.productDetails[0]?.currency ||
                DEFAULT_CURRENCY_CODE
            ),
            items: [
              {
                item_id: (
                  configProductData?.productDetails[0]?.material ||
                  configProductData?.productDetails[0]?.materialNumber ||
                  EventValues.Empty
                )?.toLowerCase(),
                item_name: (
                  configProductData?.productName
                    .replace(regexForStripEncodingUnicode, '')
                    .replace(regexForStripHTML, '')
                    .replace(regexForStripSpecialChars, '') || EventValues.Empty
                )?.toLowerCase(),
                discount: configProductData?.discount || 0,
                item_brand: (
                  configProductData?.productDetails[0]?.brand ||
                  EventValues.Empty
                )?.toLowerCase(),
                item_list_id: configToolStr,
                item_list_name: configToolStr,
                item_variant: (
                  configProductData?.productDetails[0]?.material ||
                  EventValues.Empty
                )?.toLowerCase(),
                price: priceFormatter(configProductData?.price),
                item_type: 'standard',
                quantity: configProductData?.totalQuantity || 1,
              },
            ],
          },
        },
      })
      window?.optimizely?.push({
        type: 'event',
        eventName: 'add_to_cart',
        tags: {
          addtocart_type: 'default',
          quantity: configProductData?.totalQuantity || 1,
          price: priceFormatter(configProductData?.price) ?? 0,
        },
      })
    } else if (
      configProductData?.productName === ConfiguratorProductType.Sirna
    ) {
      const trackingData = configProductData.productDetails
        .map((item) => item.subSetInfo)
        .flat()
      const productArray = clubbingUpProductsforConfig(
        configProductData,
        trackingData
      )

      // GA4 - Add To Cart
      legacySendEvent({
        payload: {
          event: 'add_to_cart',
          addtocart_type: 'default',
          ecommerce: {
            value: subTotal,
            currency: getCurrencyCode(
              configProductData.productDetails[0]?.currency ||
                DEFAULT_CURRENCY_CODE
            ),
            items: productArray.map((product) => ({
              item_id: (product.id || EventValues.Empty)?.toLowerCase(),
              item_name: (product.name || EventValues.Empty)?.toLowerCase(),
              discount: product?.discount || 0,
              item_brand: (product.brand || EventValues.Empty)?.toLowerCase(),
              item_list_id: configToolStr,
              item_list_name: configToolStr,
              item_variant: (
                product.variant || EventValues.Empty
              )?.toLowerCase(),
              price: priceFormatter(product?.price),
              item_type: 'standard',
              quantity: product.quantity || 1,
            })),
          },
        },
      })
      window?.optimizely?.push({
        type: 'event',
        eventName: 'add_to_cart',
        tags: {
          addtocart_type: 'default',
          quantity: productArray.reduce(
            (acc, product) => acc + (product.quantity ?? 1),
            0
          ),
          price: productArray.reduce(
            (acc, product) =>
              acc + (product.quantity ?? 1) * (Number(product?.price) || 1),
            0
          ),
        },
      })
    } else {
      // GAtracking for add To cart for configurator product Milliplex
      const addtocartPayload = {
        productIds:
          configProductData.milliplexData[0]?.listMilliplexAssayModel[0]?.assays
            ?.filter((assay) =>
              configProductData.milliplexData[0]?.checkedProductIdList.includes(
                assay.panelId
              )
            )
            .map((assay) => assay.material?.toLowerCase())
            .join('|') || EventValues.Empty,
        actionTitle: GTMEventCategory.ConfiguratorTool,
        ecommerce: {
          currencyCode: getCurrencyCode(
            configProductData.milliplexData[0]?.currency ||
              DEFAULT_CURRENCY_CODE
          ),
          add: {
            actionField: {
              list: GTMEventCategory.ConfiguratorTool
                ? GTMEventCategory.ConfiguratorTool.toLowerCase()
                : EventValues.Empty,
            },
            products:
              configProductData.milliplexData[0]?.listMilliplexAssayModel[0]?.assays
                ?.filter((assay) =>
                  configProductData.milliplexData[0]?.checkedProductIdList.includes(
                    assay.panelId
                  )
                )
                .map((assay) => {
                  let gaPrice
                  let gaFormattedPrice = '1'
                  if (assay?.price) {
                    const avgPrice = assay?.price
                    if (avgPrice) {
                      try {
                        gaPrice = avgPrice
                          .slice(0, avgPrice.length - 3)
                          .replace(/[.,]/g, '')
                        gaFormattedPrice =
                          gaPrice +
                          avgPrice.slice(avgPrice.length - 3, avgPrice.length)
                      } catch {
                        gaFormattedPrice = avgPrice
                      }
                    } else {
                      gaFormattedPrice = avgPrice
                    }
                  }
                  return {
                    id: assay.material
                      ? assay.material.toLowerCase()
                      : EventValues.Empty,
                    name: assay.panelName
                      ? assay.panelName
                          .toLowerCase()
                          .replace(regexForStripEncodingUnicode, '')
                          .replace(regexForStripHTML, '')
                          .replace(regexForStripSpecialChars, '')
                      : EventValues.Empty,
                    variant: assay.material
                      ? assay.material.toLowerCase()
                      : EventValues.Empty,
                    brand:
                      configProductData.milliplexData[0] &&
                      configProductData.milliplexData[0].brand
                        ? configProductData.milliplexData[0].brand.toLowerCase()
                        : EventValues.Empty,
                    quantity: assay.quantity ? assay.quantity : 1,
                    price: priceFormatter(gaFormattedPrice),
                    dimension91: 'standard',
                  }
                }) || [],
          },
        },
      }

      // GA4 - Add To Cart
      legacySendEvent({
        payload: {
          event: 'add_to_cart',
          addtocart_type: 'default',
          ecommerce: {
            value: subTotal,
            currency: getCurrencyCode(
              configProductData.milliplexData[0]?.currency ||
                DEFAULT_CURRENCY_CODE
            ),
            items: addtocartPayload.ecommerce.add.products.map((product) => ({
              item_id: (product.id || EventValues.Empty)?.toLowerCase(),
              item_name: (product.name || EventValues.Empty)?.toLowerCase(),
              discount: product?.discount || 0,
              item_brand: (product.brand || EventValues.Empty)?.toLowerCase(),
              item_list_id: configToolStr,
              item_list_name: configToolStr,
              item_variant: (
                product.variant || EventValues.Empty
              )?.toLowerCase(),
              price: priceFormatter(product?.price),
              item_type: 'standard',
              quantity: product.quantity || 1,
            })),
          },
        },
      })
      window?.optimizely?.push({
        type: 'event',
        eventName: 'add_to_cart',
        tags: {
          addtocart_type: 'default',
          quantity: addtocartPayload.ecommerce.add.products.reduce(
            (acc, product) => acc + (product.quantity ?? 1),
            0
          ),
          price: addtocartPayload.ecommerce.add.products.reduce(
            (acc, product) =>
              acc + (product.quantity ?? 1) * (Number(product?.price) || 1),
            0
          ),
        },
      })
    }
  }
}

export const handleGAAddToCartErrorsForConfigurator = (
  configProductData,
  errorMessage
): void => {
  const productName = configProductData?.productName
  const eventLabel =
    `${productName?.toLowerCase()} - ${errorMessage}` || EventValues.Empty
  if (isTealiumEnabled) {
    const products = [
      {
        eventAction: EventActions.AddToCartErrors,
        eventCategory: GTMEventCategory.Errors?.toLowerCase(),
        eventLabel: eventLabel?.toLowerCase(),
      },
    ]

    sendAddItemsToCartErrorEvent(products)
  }

  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: GTMEventCategory.AddToCartErrors,
      eventAction: EventActions.AddToCartErrors?.toLowerCase(),
      eventLabel: eventLabel?.toLowerCase(),
      eventInteractionType: 1,
    },
  })
}

export const sendConfiguratorUserInteractionGa4Event = (
  ga4payload?: GA4ListInteractionEventPayload
): void => {
  const basePayload = {
    event: ga4payload?.event?.toLowerCase(),
    action: ga4payload?.action?.toLowerCase(),
    detail: ga4payload?.detail?.toLowerCase() || undefined,
    section: ga4payload?.section?.toLowerCase() || undefined,
    component: ga4payload?.component?.toLowerCase(),
    element_type: ga4payload?.elementType?.toLowerCase(),
    element_text: ga4payload?.elementText?.toLowerCase(),
    link_url: ga4payload?.linkUrl?.toLowerCase() || undefined,
    core_event: ga4payload?.coreEvent?.toLowerCase() || 'no',
    user_detail: ga4payload?.userDetail?.toLowerCase() || undefined,
    event_group: ga4payload?.eventGroup?.toLowerCase() || undefined,
    event_subgroup: ga4payload?.eventSubGroup?.toLowerCase() || undefined,
    product_id: ga4payload?.productId?.toLowerCase() || undefined,
    product_brand: ga4payload?.productBrand?.toLowerCase() || undefined,
    product_variant: ga4payload?.productVariant?.toLowerCase() || undefined,
    product_name: ga4payload?.productName?.toLowerCase() || undefined,
    key_event: ga4payload?.keyEvent?.toLowerCase() || undefined,
  }
  //console.log('Batman basePayload *** ', JSON.stringify(basePayload, null, 2))
  // legacySendEvent({
  //   payload: basePayload,
  // })
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(basePayload)
}

export const sendAddToCartConfiguratorEvent = (
  products: ConfiguratorEventProduct[],
  currency: string | null | undefined
): void => {
  // Group products by ID so multi-entry uploads collapse into one line item
  const grouped = groupBy(products, (p) => p.id)

  // Build GTM-style items array
  const items = Object.values(grouped).map((group) => {
    const totalQty = group.reduce((sum, p) => sum + (p.quantity ?? 1), 0)
    const totalValue = group.reduce(
      (sum, p) => sum + (Number(p.price) || 0) * (p.quantity ?? 1),
      0
    )
    const avgPrice = totalQty > 0 ? totalValue / totalQty : 0

    return {
      item_id: group[0].id,
      item_name: group[0].name,
      discount: 0.0, // spec: always 0.00
      item_brand: group[0].brand,
      item_list_id: configToolStr,
      item_list_name: configToolStr,
      item_variant: group[0].variant || EventValues.Empty,
      price: priceFormatter(avgPrice),
      item_type: group[0].dimension91,
      quantity: totalQty,
    }
  })

  // Compute total value across all items
  const totalValue = items.reduce(
    (sum, i) => sum + (i.price ?? 0) * i.quantity,
    0
  )

  // Build your GTM payload
  const payload = {
    event: 'add_to_cart',
    addtocart_type: 'default',
    ecommerce: {
      currency: currency ? getCurrencyCode(currency) : null,
      value: totalValue,
      items,
    },
  }

  // Fire the GA4 event
  sendEvent({ payload })

  // Send to Optimizely if you need it
  window?.optimizely?.push({
    type: 'event',
    eventName: 'add_to_cart',
    tags: {
      addtocart_type: 'default',
      quantity: items.reduce((sum, i) => sum + i.quantity, 0),
      price: totalValue,
    },
  })
}

export const sendConfiguratorGADownloadEvent = (
  action: string,
  label: string
): void => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventValues.Downloads.toLowerCase(),
      eventAction:
        EventActions.Templates === action
          ? `${EventActions.Configurator} ${EventActions.Templates}`
          : `${EventActions.Configurator} ${EventActions.Summary}`,
      eventLabel:
        EventActions.Templates === action
          ? `${label} ${EventLabel.Template}`
          : `${label} ${EventActions.Summary}`,
      eventInteractionType: 0,
    },
  })
}
